import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Flex,
  Text,
  Button,
  ButtonGroup,
  Badge,
  Input,
  Spinner,
  Switch,
  Tooltip,
  IconButton,
  Divider,
  useToast,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaBell, FaTrash, FaPlus } from "react-icons/fa";

const API = process.env.REACT_APP_API_URL;

// The two SOH stock alerts. `id` is what the API expects as
// `alert`; these are the only two runners the backend whitelists.
const ALERT_TABS = [
  { id: "pnp", label: "Pick n Pay", retailer: "PNP" },
  { id: "chk", label: "Checkers", retailer: "CHK" },
];

// Which suppliers each stock alert watches. The alert scripts read this list at
// run time, so a change here takes effect on the next run — scheduled or
// on-demand from the Alerts tab.
const AlertSuppliers = () => {
  const toast = useToast();
  const userid = localStorage.getItem("userid");

  const [alertId, setAlertId] = useState("pnp");
  const [tracked, setTracked] = useState([]);
  const [available, setAvailable] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [toAdd, setToAdd] = useState("");
  // Supplier code currently being written, so only that row shows a spinner.
  const [busyCode, setBusyCode] = useState(null);
  const [adding, setAdding] = useState(false);

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const rowHoverBg = useColorModeValue("gray.50", "gray.700");
  const mutedColor = useColorModeValue("gray.500", "gray.400");

  const activeAlert = ALERT_TABS.find((a) => a.id === alertId) || ALERT_TABS[0];

  const load = useCallback(
    async (id) => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const res = await axios.post(`${API}/alerts/suppliers`, { userid, alert: id });
        const data = res.data?.data || {};
        setTracked(data.tracked || []);
        setAvailable(data.available || []);
      } catch (e) {
        const msg = e?.response?.data?.message || e.message || "Failed to load alert suppliers";
        setLoadError(msg);
        setTracked([]);
        setAvailable([]);
      } finally {
        setIsLoading(false);
      }
    },
    [userid],
  );

  useEffect(() => {
    setToAdd("");
    load(alertId);
  }, [alertId, load]);

  const activeCount = useMemo(() => tracked.filter((t) => t.activeyn).length, [tracked]);

  // Every write re-reads the list rather than patching local state, so the
  // panel always reflects what the alert scripts will actually read.
  const mutate = async (endpoint, body, successMsg, code) => {
    setBusyCode(code || null);
    try {
      await axios.post(`${API}/alerts/suppliers/${endpoint}`, { userid, alert: alertId, ...body });
      await load(alertId);
      toast({ status: "success", title: successMsg, duration: 3000, isClosable: true });
      return true;
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Request failed";
      toast({ status: "error", title: "Update failed", description: msg, duration: 6000, isClosable: true });
      return false;
    } finally {
      setBusyCode(null);
    }
  };

  const handleAdd = async () => {
    const code = toAdd.trim().toUpperCase();
    if (!code || adding) return;
    setAdding(true);
    const ok = await mutate("add", { suppliercode: code }, `${code} added to the ${activeAlert.label} alert`, code);
    if (ok) setToAdd("");
    setAdding(false);
  };

  const handleToggle = (row) =>
    mutate(
      "toggle",
      { suppliercode: row.suppliercode, activeyn: !row.activeyn },
      `${row.suppliercode} ${row.activeyn ? "paused" : "resumed"}`,
      row.suppliercode,
    );

  const handleRemove = (row) =>
    mutate("remove", { suppliercode: row.suppliercode }, `${row.suppliercode} removed`, row.suppliercode);

  // null = the code isn't in the retailer's articles table at all, so the alert
  // can never match a row for it. 0 = present but no articles. Both are worth
  // surfacing; anything else is healthy.
  const articlesBadge = (articles) => {
    if (articles === null || articles === undefined) {
      return (
        <Tooltip label={`Not found in ${activeAlert.retailer.toLowerCase()}_articles — this supplier can never match a row`}>
          <Badge colorScheme="red" variant="subtle" fontSize="0.65rem">no articles</Badge>
        </Tooltip>
      );
    }
    if (articles === 0) {
      return (
        <Tooltip label="Present but has 0 articles — nothing to match">
          <Badge colorScheme="orange" variant="subtle" fontSize="0.65rem">0 articles</Badge>
        </Tooltip>
      );
    }
    return (
      <Badge colorScheme="gray" variant="subtle" fontSize="0.65rem">
        {articles} article{articles === 1 ? "" : "s"}
      </Badge>
    );
  };

  return (
    <Box mt={4} bg={cardBg} border="1px" borderColor={borderColor} borderRadius="lg" boxShadow="sm" p={5}>
      <Flex align="center" justify="space-between" mb={1} wrap="wrap" gap={3}>
        <Flex align="center" gap={3}>
          <FaBell />
          <Box>
            <Text fontWeight="bold" fontSize="md">Stock Alert Suppliers</Text>
            <Text fontSize="sm" color={mutedColor}>
              Suppliers watched by each SOH alert. Changes apply on the next run.
            </Text>
          </Box>
        </Flex>
        <ButtonGroup size="sm" isAttached variant="outline">
          {ALERT_TABS.map((a) => (
            <Button
              key={a.id}
              onClick={() => setAlertId(a.id)}
              colorScheme={alertId === a.id ? "green" : "gray"}
              variant={alertId === a.id ? "solid" : "outline"}
            >
              {a.label}
            </Button>
          ))}
        </ButtonGroup>
      </Flex>

      <Divider my={4} />

      {/* Add control. A datalist rather than a plain <select> so a supplier can
          be tracked before its articles land — AMAZINGPIES was exactly that
          case. Typed codes are accepted; the backend trims + uppercases, and a
          code with no articles is flagged in the list below. */}
      <Flex gap={2} mb={4} wrap="wrap">
        <Input
          size="sm"
          maxW="360px"
          list="alert-supplier-options"
          placeholder="Supplier code — pick from the list or type a new one"
          value={toAdd}
          onChange={(e) => setToAdd(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleAdd();
          }}
          isDisabled={isLoading}
        />
        <datalist id="alert-supplier-options">
          {available.map((s) => (
            <option key={s.suppliercode} value={s.suppliercode}>
              {`${s.articles} article${s.articles === 1 ? "" : "s"}`}
            </option>
          ))}
        </datalist>
        <Button
          size="sm"
          leftIcon={<FaPlus />}
          colorScheme="green"
          onClick={handleAdd}
          isDisabled={!toAdd.trim() || isLoading}
          isLoading={adding}
          loadingText="Adding…"
        >
          Add
        </Button>
      </Flex>

      <Flex justify="space-between" align="center" mb={2} wrap="wrap" gap={2}>
        <Text fontSize="sm" fontWeight="medium">
          {activeAlert.label} alert — {activeCount} active
          {tracked.length !== activeCount ? ` of ${tracked.length} tracked` : ""}
        </Text>
      </Flex>

      {isLoading ? (
        <Flex justify="center" align="center" py={10}><Spinner /></Flex>
      ) : loadError ? (
        <Flex direction="column" justify="center" align="center" py={10} gap={3}>
          <Text fontSize="sm" color="red.400">{loadError}</Text>
          <Button size="xs" variant="outline" onClick={() => load(alertId)}>Retry</Button>
        </Flex>
      ) : tracked.length === 0 ? (
        <Flex justify="center" align="center" py={10} px={4}>
          <Text fontSize="sm" color="orange.400" textAlign="center">
            No suppliers are being watched — the {activeAlert.label} alert will report nothing. Add one above.
          </Text>
        </Flex>
      ) : (
        <Box maxH="320px" overflowY="auto" pr={2}>
          {tracked.map((row) => (
            <Flex
              key={row.suppliercode}
              align="center"
              justify="space-between"
              py={2}
              px={3}
              borderRadius="md"
              _hover={{ bg: rowHoverBg }}
              opacity={row.activeyn ? 1 : 0.55}
            >
              <Flex align="center" gap={3} minW={0}>
                <Tooltip label={row.activeyn ? "Watched — switch off to pause" : "Paused — switch on to resume"}>
                  <Box>
                    <Switch
                      size="sm"
                      colorScheme="green"
                      isChecked={row.activeyn}
                      onChange={() => handleToggle(row)}
                      isDisabled={busyCode === row.suppliercode}
                    />
                  </Box>
                </Tooltip>
                <Text fontSize="sm" fontWeight="medium" noOfLines={1}>{row.suppliercode}</Text>
                {articlesBadge(row.articles)}
                {!row.activeyn && (
                  <Badge colorScheme="yellow" variant="subtle" fontSize="0.65rem">paused</Badge>
                )}
              </Flex>
              <Flex align="center" gap={2}>
                {busyCode === row.suppliercode && <Spinner size="xs" />}
                <Tooltip label="Remove from this alert">
                  <IconButton
                    aria-label={`Remove ${row.suppliercode}`}
                    icon={<FaTrash />}
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => handleRemove(row)}
                    isDisabled={busyCode === row.suppliercode}
                  />
                </Tooltip>
              </Flex>
            </Flex>
          ))}
        </Box>
      )}

      <Text fontSize="xs" color={mutedColor} mt={4}>
        Pausing keeps the supplier on the list but excludes it from the report — use it for lapsed
        subscriptions. Removing deletes the entry entirely. Run the alerts from the <strong>Alerts</strong> tab.
      </Text>
    </Box>
  );
};

export default AlertSuppliers;
